import React, { useState } from 'react';
import { Shield, Users, Stethoscope, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing page — the only screen an unauthenticated visitor sees.
 *
 * VetFlow is staff-only, so there is a single sign-in: "Sign in with
 * Microsoft" starts the staff Azure OAuth flow via AuthContext. The
 * client portal was retired with the insurance claims domain (archived
 * on the archive/insurance-claims branch).
 */

const MicrosoftIcon = () => (
  <svg className="h-5 w-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1"  y="1"  width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1"  width="9" height="9" fill="#7FBA00"/>
    <rect x="1"  y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const HomePage = ({ navigateTo }) => {
  const { loginWithMicrosoft, authError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleStaffLogin = async () => {
    setIsLoading(true);
    await loginWithMicrosoft();
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Stethoscope className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">VetFlow</h1>
                <p className="text-sm text-gray-500">Veterinary Practice Management</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
              <Heart className="h-10 w-10 text-blue-600" />
            </div>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Welcome to VetFlow
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Sign in to access our comprehensive veterinary practice management system
          </p>
          {authError && (
            <div className="mt-6 mx-auto max-w-md bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {authError}
            </div>
          )}
        </div>

        {/* Sign-in — a single staff card, centred rather than a half-width
            grid cell now that the client portal is gone. */}
        <div className="max-w-lg mx-auto">
          {/* Staff Portal Card */}
          <div className="group relative bg-white rounded-2xl shadow-xl border-2 border-gray-200 transition-all duration-300 hover:shadow-2xl hover:border-blue-300">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-8 h-full flex flex-col">
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-20 h-20 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-6 transition-all duration-300">
                  <Users className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Staff Portal</h3>
                <p className="text-gray-600 mb-8 leading-relaxed min-h-[4.5rem]">
                  For veterinary staff and administrators to manage appointments, patient records, and operations
                </p>
                <button
                  onClick={handleStaffLogin}
                  disabled={isLoading}
                  className="flex items-center justify-center px-8 py-3 rounded-lg font-semibold bg-white border-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:shadow-md transition-all duration-200 w-full max-w-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Redirecting to Microsoft...
                    </>
                  ) : (
                    <>
                      <MicrosoftIcon />
                      Sign in with Microsoft
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Features Section */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl font-semibold text-gray-900 mb-8">Why Choose VetFlow?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Secure</h4>
              <p className="text-gray-600 text-sm">Enterprise-grade security for your data</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">User-Friendly</h4>
              <p className="text-gray-600 text-sm">Intuitive interface designed for veterinary practices</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Stethoscope className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Comprehensive</h4>
              <p className="text-gray-600 text-sm">All tools you need in one platform</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            © 2024 VetFlow. All rights reserved. Built with ❤️ for veterinary professionals.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
